import React, { useEffect, useMemo, useState } from "react";

import { Switch } from "@packages/ds";
import moment from "moment";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import { ACTIVITY_CONTEXT } from "Constants/constants";
import * as Sticker from "ds/Sticker";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import getUrl from "Libs/getUrl";
import {
  loadMoreProjectActivities,
  loadProjectActivities,
  loadEnvironmentActivities,
  loadMoreEnvironmentActivities,
  loadIntegrationActivities,
  loadMoreIntegrationActivities,
  getSelectors,
  getActivityFilters,
  Context,
  selectDeployInProgressActivity,
  loadEnvironmentStagedActivities,
  selectEnvironmentStagedActivities
} from "Reducers/activity";
import { environmentSelector } from "Reducers/environment";
import { deploymentSizingApiEnabledSelector } from "Reducers/environment/deployment";
import { gitProjectSelector } from "Reducers/project/project";
import { useDeploymentType } from "Reducers/project/project/hooks/useDeploymentType";
import ResourcesSticker from "src/common/components/EnvironmentTree/ResourcesSticker";
import ActivityList from "src/organization/common/components/ActivityList";
import Filter from "src/organization/common/components/ActivityList/Filter";
import LogModal from "src/organization/common/components/ActivityList/LogModal";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import InfoDropButtons from "../InfoDropButtons";

import * as S from "./ActivitySection.styles";
import ActivitySectionSkeleton from "./ActivitySectionSkeleton";
import { filterOptions, allFilterNames } from "./util";

import type { Activity } from "@packages/client";

const toggleActiveFilterNames = (
  appliedFilters: string[] = [],
  filterName: string | null = null
) => {
  if (appliedFilters.includes(filterName ?? "0"))
    return appliedFilters.filter(name => name !== filterName);
  return filterName ? [...appliedFilters, filterName] : appliedFilters;
};

const ellapsedTimeIsLessThan = (date, durationInSeconds) => {
  const duration = moment.duration(moment().diff(moment(date)));
  return durationInSeconds > duration.asSeconds();
};

/**
 * Filter activities not older than ellapsedTime and limit by limitActivityCount
 * @param {Activities} activities Activities to filter
 * @param {number} ellapsedSeconds Time in seconds by which this activity must not be older than
 * @param {number} limitActivityCount Total activities to be considered in created date ASC
 * @returns Activities filtered by ellapsed time and limit
 */
export const filterRecentActivities = (
  activities: Activity[],
  ellapsedSeconds: number = Infinity,
  limitActivityCount: number = Infinity
) => {
  const limitCount =
    activities.length < limitActivityCount
      ? activities.length
      : limitActivityCount;
  return activities
    .sort(
      (item1, item2) =>
        new Date(item2.created_at).getTime() -
        new Date(item1.created_at).getTime()
    )
    .slice(0, limitCount)
    .filter(({ created_at }) =>
      ellapsedTimeIsLessThan(created_at, ellapsedSeconds)
    );
};

type Props = {
  context: Context;
  showResourcesSticker?: boolean;
  excludeActivies?: string[];
};

const ActivitySection = ({
  context: ctx,
  showResourcesSticker,
  excludeActivies = []
}: Props) => {
  const context = ctx.split(".")[0];
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { appliedFilters } = useAppSelector(state => {
    const filter = getActivityFilters(state, context);
    return {
      appliedFilters: filter ? filter : allFilterNames
    };
  });
  const [openedCancelModal, setOpenedCancelModal] = useState("");
  const [selectedFailedActivityLog, setSelectedFailedActivityLog] =
    useState("");
  const [shouldShowFailedActivitiesOnly, _setShouldShowFailedActivitiesOnly] =
    useState(false);
  const [isOnScroll, setIsOnScroll] = useState(false);

  function setShouldShowFailedActivitiesOnly(value) {
    if (
      shouldShowFailedActivitiesOnly === false &&
      value === true &&
      !selectedFailedActivityLog
    ) {
      setSelectedFailedActivityLog(failedActivities?.[0].id);
    }

    _setShouldShowFailedActivitiesOnly(value);
  }

  const {
    projectId,
    organizationId,
    environmentId,
    integrationId,
    activityId
  } = useDecodedParams<{
    projectId: string;
    organizationId: string;
    environmentId: string;
    integrationId: string;
    activityId: string;
  }>();
  const { shouldShowDeploymentType, isAutomaticDeploymentEnabled } =
    useDeploymentType();
  const {
    completed,
    inProgress,
    pending,
    hasMore,
    failed,
    isLoading,
    isLoadingMore
  } = getSelectors(context as Context, {
    projectId,
    organizationId,
    environmentId,
    integrationId
  });
  const [
    projectOverviewUrl,
    projectLogUrl,
    envOverviewUrl,
    envLogUrl,
    projectIntegrationUrl,
    projectIntegrationLogUrl
  ] = useUrls(
    [
      "organization.project",
      "organization.project.log",
      "organization.project.environment",
      "organization.project.environment.log",
      "organization.project.settings.integrations.integration",
      "organization.project.settings.integrations.integration.log"
    ],
    {
      organizationId,
      projectId,
      environmentId: environmentId ?? "",
      activityId: activityId ?? "",
      integrationId: integrationId ?? ""
    }
  );

  const deployActionsInProgress = useAppSelector(state =>
    selectDeployInProgressActivity(state, {
      projectId,
      organizationId,
      environmentId
    })
  );
  const isDeployActionsInProgress = deployActionsInProgress.length > 0;

  const closeModal = () => {
    const urls = {
      [projectLogUrl]: projectOverviewUrl,
      [envLogUrl]: envOverviewUrl,
      [projectIntegrationLogUrl]: projectIntegrationUrl
    };

    navigate(urls[location.pathname] ?? "/");
  };

  const environment = useSelectorWithUrlParams(environmentSelector);
  const isEnvPaused = environment?.status === "paused";
  const isEnvStatusDirty = environment?.status === "dirty";
  const pendingActivity = useAppSelector(pending);
  const stagedActivities = useSelectorWithUrlParams(
    selectEnvironmentStagedActivities
  );
  const recentPendingActivities = useMemo(
    () => filterRecentActivities(pendingActivity),
    [pendingActivity]
  );
  const recentStagedActivities = useMemo(
    () => filterRecentActivities(stagedActivities),
    [stagedActivities]
  );

  useMemo(() => filterRecentActivities(pendingActivity), [pendingActivity]);

  const completeActivities = useAppSelector(completed);
  const recentCompletedActivities = useMemo(
    () => filterRecentActivities(completeActivities),
    [completeActivities]
  );
  const inProgressActivity = useAppSelector(inProgress);
  const recentInprogressActivities = useMemo(
    () => filterRecentActivities(inProgressActivity),
    [inProgressActivity]
  );

  const failedActivities = useAppSelector(failed);
  const project = useSelectorWithUrlParams(gitProjectSelector);
  const hasMoreActivities = useAppSelector(hasMore);
  const isLoadingActivities = useAppSelector(isLoading);
  const isLoadingMoreActivities = useAppSelector(isLoadingMore);
  const isSizingApiEnabled = useAppSelector(state => {
    return deploymentSizingApiEnabledSelector(state, {
      organizationId,
      projectId,
      environmentId
    });
  });

  const getFilterNames = (toggleName: string) => {
    if (toggleName == "all_type") {
      return appliedFilters.includes("all_type") ? [] : allFilterNames;
    }

    const filterNames = toggleActiveFilterNames(appliedFilters, toggleName);
    const filterWithoutAll = filterNames.filter(n => n !== "all_type");
    return filterWithoutAll.length === allFilterNames.length - 1
      ? allFilterNames
      : filterNames.filter(name => name !== "all_type");
  };

  const loadActivity = (filterName: string) => {
    const filter = getFilterNames(filterName);
    switch (context) {
      case ACTIVITY_CONTEXT.Environment:
        dispatch(
          loadEnvironmentActivities({
            projectDescriptionId: projectId,
            environmentDescriptionId: environmentId,
            organizationDescriptionId: organizationId,
            filterNames: filter,
            context
          })
        );
        break;
      case ACTIVITY_CONTEXT.Integration:
        if (!integrationId) break;
        dispatch(
          loadIntegrationActivities({
            organizationDescriptionId: organizationId,
            projectDescriptionId: projectId,
            integrationDescriptionId: integrationId,
            filterNames: filter,
            context
          })
        );
        break;
      case ACTIVITY_CONTEXT.Project:
        if (project?.id) {
          dispatch(
            loadProjectActivities({
              projectDescriptionId: projectId,
              organizationDescriptionId: organizationId,
              filterNames: filter,
              context
            })
          );
        }
    }
  };

  const loadMoreActivity = () => {
    if (!hasMoreActivities) return;
    const filter = getFilterNames("");

    switch (context) {
      case ACTIVITY_CONTEXT.Environment:
        dispatch(
          loadMoreEnvironmentActivities({
            projectDescriptionId: projectId,
            environmentDescriptionId: environmentId,
            organizationDescriptionId: organizationId,
            filterNames: filter
          })
        );
        break;
      case ACTIVITY_CONTEXT.Integration:
        dispatch(
          loadMoreIntegrationActivities({
            organizationDescriptionId: organizationId,
            projectDescriptionId: projectId,
            integrationDescriptionId: integrationId,
            filterNames: filter
          })
        );
        break;
      default:
        if (project?.id) {
          dispatch(
            loadMoreProjectActivities({
              projectDescriptionId: project.id,
              organizationDescriptionId: organizationId,
              filterNames: filter
            })
          );
        }
    }
  };
  useEffect(() => {
    context === ACTIVITY_CONTEXT.Environment &&
      isAutomaticDeploymentEnabled &&
      dispatch(
        loadEnvironmentStagedActivities({
          projectDescriptionId: projectId,
          environmentDescriptionId: environmentId,
          organizationDescriptionId: organizationId,
          context
        })
      );
  }, [
    environmentId,
    projectId,
    organizationId,
    isAutomaticDeploymentEnabled,
    context,
    dispatch
  ]);

  useEffect(() => {
    loadActivity("crons");
    // Reason: Adding "loadActivity" to the dependency array causes infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentId, project?.id, integrationId]);

  const canEditProject = !!project?.hasPermission("#edit");

  const options = useMemo(() => {
    return filterOptions.filter(
      option =>
        (isSizingApiEnabled || option.label !== "resource_allocation_filter") &&
        !excludeActivies.includes(option.label)
    );
  }, [isSizingApiEnabled, excludeActivies]);

  return (
    <S.Container
      aria-labelledby="project-activity-heading"
      onScroll={event => {
        const { scrollTop, scrollHeight, clientHeight } =
          event.target as unknown as {
            scrollTop: number;
            scrollHeight: number;
            clientHeight: number;
          };
        setIsOnScroll(scrollTop > 10);
        if (scrollHeight - scrollTop === clientHeight) {
          loadMoreActivity();
        }
      }}
    >
      {showResourcesSticker && (
        <ResourcesSticker
          environmentTitle={environment?.title!}
          url={getUrl({
            key: "organization.project.environment.configureResources",
            props: {
              organizationId,
              projectId,
              environmentId: environmentId || "",
              location: location.pathname + location.search,
              queryParams: ""
            }
          })}
        />
      )}
      <S.InfoButtonWrapper>
        {project && context === "environment" && (
          <InfoDropButtons project={project} />
        )}
      </S.InfoButtonWrapper>
      <S.ActivityHeader $isOnScroll={isOnScroll}>
        <S.ActivityTitle id="project-activity-heading" tag="h2">
          {intl.formatMessage({ id: "activity" })}
        </S.ActivityTitle>
        <S.LeftSection>
          {failedActivities.length !== 0 && (
            <Switch
              label={intl.formatMessage({
                id: "activity.show_failed_activities_only"
              })}
              checked={shouldShowFailedActivitiesOnly}
              onCheckedChange={setShouldShowFailedActivitiesOnly}
            />
          )}
          <Filter
            options={options}
            activeOptions={appliedFilters}
            onChange={loadActivity}
          />
        </S.LeftSection>
      </S.ActivityHeader>
      {shouldShowFailedActivitiesOnly ? (
        isLoadingActivities ? (
          <ActivitySectionSkeleton />
        ) : (
          <S.BoxLayout>
            <ActivityList
              setSelectedFailedActivityLog={setSelectedFailedActivityLog}
              selectedFailedActivityLog={selectedFailedActivityLog}
              shouldShowLog={shouldShowFailedActivitiesOnly}
              organizationId={organizationId}
              activities={failedActivities}
              activityType="running"
              activityIsLoading={isLoadingMoreActivities}
              canEditProject={canEditProject}
              openedCancelModal={openedCancelModal}
              setOpenedCancelModal={setOpenedCancelModal}
            />
          </S.BoxLayout>
        )
      ) : (
        <>
          {isEnvPaused && !isEnvStatusDirty && (
            <S.StickerRoot priority="information">
              <Sticker.Icon priority="information" />
              <S.StickerText>
                {intl.formatMessage<React.ReactNode>(
                  {
                    id: "activity.sticker.paused.environment"
                  },
                  {
                    ctaLink: chunks => (
                      <S.Button
                        analyticId="organization.project.environment.actions.resume"
                        content="text"
                        variant="link"
                        onClick={event => {
                          navigate(
                            getUrl({
                              key: "organization.project.environment.actions.resume",
                              props: {
                                organizationId,
                                projectId,
                                environmentId
                              }
                            })
                          );
                          event.preventDefault();
                        }}
                      >
                        {chunks}
                      </S.Button>
                    ),
                    infoLink: chunks => (
                      <S.Link
                        href={intl.formatMessage({
                          id: "links.documentation.paused_environment"
                        })}
                        analyticId="links.documentation.paused_environment"
                      >
                        {chunks}
                      </S.Link>
                    )
                  }
                )}
              </S.StickerText>
            </S.StickerRoot>
          )}
          <S.SubsectionHeadingFirst>
            {intl.formatMessage({ id: "activities.headers.running" })}
            <S.ActivityCount>
              {" "}
              ({recentInprogressActivities.length})
            </S.ActivityCount>
          </S.SubsectionHeadingFirst>
          {isLoadingActivities ? (
            <ActivitySectionSkeleton />
          ) : (
            <S.BoxLayout>
              <ActivityList
                shouldShowLog={shouldShowFailedActivitiesOnly}
                organizationId={organizationId}
                activities={recentInprogressActivities}
                activityType="running"
                activityIsLoading={isLoadingMoreActivities}
                canEditProject={canEditProject}
                openedCancelModal={openedCancelModal}
                setOpenedCancelModal={setOpenedCancelModal}
              />
            </S.BoxLayout>
          )}
          <S.SubsectionHeading>
            {intl.formatMessage({ id: "activities.headers.pending" })}
            {!(isLoadingActivities || isLoadingMoreActivities) && (
              <S.ActivityCount>
                {" "}
                ({recentPendingActivities.length})
              </S.ActivityCount>
            )}
          </S.SubsectionHeading>
          {isLoadingActivities ? (
            <>
              <ActivitySectionSkeleton />
              <ActivitySectionSkeleton />
            </>
          ) : (
            <S.BoxLayout>
              <ActivityList
                shouldShowLog={shouldShowFailedActivitiesOnly}
                organizationId={organizationId}
                activities={recentPendingActivities}
                activityType="pending"
                activityIsLoading={isLoadingMoreActivities}
                canEditProject={canEditProject}
                openedCancelModal={openedCancelModal}
                setOpenedCancelModal={setOpenedCancelModal}
              />
            </S.BoxLayout>
          )}
          {shouldShowDeploymentType &&
            !isAutomaticDeploymentEnabled &&
            context === "environment" && (
              <>
                <S.SubsectionHeading>
                  {intl.formatMessage({ id: "activities.headers.staged" })}
                  {!(isLoadingActivities || isLoadingMoreActivities) && (
                    <S.ActivityCount>
                      {
                        <>
                          ({recentStagedActivities.length})
                          {intl.formatMessage({
                            id: "activities.headers.staged.description"
                          })}
                        </>
                      }
                    </S.ActivityCount>
                  )}
                </S.SubsectionHeading>
                {isLoadingActivities ? (
                  <>
                    <ActivitySectionSkeleton />
                    <ActivitySectionSkeleton />
                  </>
                ) : (
                  <S.BoxLayout>
                    <ActivityList
                      shouldForceLoader={isDeployActionsInProgress}
                      organizationId={organizationId}
                      activities={recentStagedActivities}
                      activityType="staged"
                      activityIsLoading={false}
                      canEditProject={canEditProject}
                      openedCancelModal={openedCancelModal}
                      setOpenedCancelModal={setOpenedCancelModal}
                    />
                  </S.BoxLayout>
                )}
              </>
            )}
          <S.SubsectionHeading>
            {intl.formatMessage({ id: "activities.headers.recent" })}{" "}
            {!(isLoadingActivities || isLoadingMoreActivities) && (
              <S.ActivityCount>
                ({recentCompletedActivities.length})
              </S.ActivityCount>
            )}
          </S.SubsectionHeading>
          {isLoadingActivities ? (
            <>
              <ActivitySectionSkeleton />
              <ActivitySectionSkeleton />
            </>
          ) : (
            <S.BoxLayout>
              <ActivityList
                shouldShowLog={shouldShowFailedActivitiesOnly}
                organizationId={organizationId}
                activities={recentCompletedActivities}
                activityIsLoading={isLoadingMoreActivities}
                activityType="all"
                canEditProject={canEditProject}
                openedCancelModal={openedCancelModal}
                setOpenedCancelModal={setOpenedCancelModal}
              />
            </S.BoxLayout>
          )}
        </>
      )}
      {activityId && <LogModal closeModal={closeModal} />}
    </S.Container>
  );
};

export default ActivitySection;
